import {
  type DefaultError,
  type InfiniteData,
  useInfiniteQuery,
  type QueryKey,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query";
import {
  useScopedMode,
  type ScopedQueryMode,
  type ScopedQueryOptions,
} from "@/hooks/use-scoped-query";
import type { QueryScope } from "@/queries/scope";

export interface ScopedInfiniteQuery<TData, TError = DefaultError> {
  readonly mode: ScopedQueryMode;
  /** Live in `"cache"` mode; otherwise a disabled query (pending, no pages). */
  readonly query: UseInfiniteQueryResult<TData, TError>;
}

/**
 * `useScopedQuery` for a cursor-paged list (contract §9): the same two modes
 * and the same `enabled`, with `useInfiniteQuery` underneath. A paged domain
 * hook (the teams page, the members table) is then the page definition from
 * `app/queries` and the rows of its pages:
 *
 *     const { query } = useScopedInfiniteQuery(slug, (scope) =>
 *       teamsPageQuery(workspaceTeamService, scope, params),
 *     );
 *     const rows = pagesToRows(query.data?.pages ?? []);
 *
 * It is a real observer of the page set: a writer's `invalidateQueries` or a
 * cache hint re-reads every page it holds, and `query.fetchNextPage` appends
 * the next one.
 *
 * `makeQuery` is called on every render, also while the read waits (with a
 * placeholder principal), so it must be a pure options builder. Reads the
 * signed-in user from a store, so the calling component must be an `observer`.
 */
export function useScopedInfiniteQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  tenant: string | undefined,
  makeQuery: (
    scope: QueryScope,
  ) => UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>,
  { enabled = true }: ScopedQueryOptions = {},
): ScopedInfiniteQuery<TData, TError> {
  const { mode, scope } = useScopedMode(tenant);
  const options = makeQuery(scope);
  const query = useInfiniteQuery({
    ...options,
    enabled: mode === "cache" && enabled ? (options.enabled ?? true) : false,
  });

  return { mode, query };
}
